import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
  KiAbdeckungLuecke,
  KiAnfrage,
  KiButtonState,
  KiEntwurfAngelegt,
  KiEntwurfArt,
  KiEntwurfStand,
  KiGesendet,
  KiSperrgrund,
  KiTestfallEntwuerfeErgebnis,
  KiTestfallEntwurf,
  KiTestfallUebernommen,
} from '../../../models/ki.model';
import { CoverageLinkDraft } from '../../../models/save-full-requirement.model';
import { KiAnfrageService } from '../../../services/ki-anfrage.service';
import { KiRunService } from '../../../services/ki-run.service';
import { KiAbdeckungComponent } from '../ki-abdeckung/ki-abdeckung.component';
import { KiButtonComponent } from '../ki-button/ki-button.component';
import { KiEntwurfCardComponent } from '../ki-entwurf-card/ki-entwurf-card.component';
import { KiErgebnisAbgelaufenComponent } from '../ki-ergebnis-abgelaufen/ki-ergebnis-abgelaufen.component';
import { KiErsteNutzungComponent } from '../ki-erste-nutzung/ki-erste-nutzung.component';
import { KiFehlerComponent } from '../ki-fehler/ki-fehler.component';
import { KiGesendetComponent } from '../ki-gesendet/ki-gesendet.component';
import { KiLaeuftComponent } from '../ki-laeuft/ki-laeuft.component';
import { KiPanelComponent } from '../ki-panel/ki-panel.component';
import { KiTestfallVorschlagDialogComponent } from '../ki-testfall-vorschlag-dialog/ki-testfall-vorschlag-dialog.component';

/** Deckungsart, die das Backend der Verknüpfung bei der Übernahme gibt; ohne oder mit unbekannter Art gilt Positiv. */
const DECKUNG: Record<KiEntwurfArt, string> = { POSITIV: 'POSITIVE', NEGATIV: 'NEGATIVE', GRENZWERT: 'EDGE_CASE' };

/** Was das Panel aus einer fertigen Anfrage zeigt; fehlt nur, wenn ihr Ergebnis nach Ablauf gelöscht ist. */
interface EntwuerfeAnsicht {
  anfrageId: string;
  entwuerfe: KiTestfallEntwurf[];
  ausgelassen: number;
}

/**
 * KI-Schaltflächen in der Karte „Testfälle“ einer Anforderung (KiAnforderungTools, page-ki.jsx:202-319):
 * „Abdeckung prüfen“ (KiAbdeckungComponent), Dialog „Testfälle vorschlagen“, Start der Anfrage und Panel
 * „Testfall-Entwürfe“ mit einer Karte je Entwurf. Jede Übernahme meldet sie der Anforderung, die den Testfall
 * in ihre Verknüpfungsliste nimmt. Der Absprung aus dem Postfach (`?kiAnfrage`) öffnet das Panel mit dem
 * gespeicherten Ergebnis, ohne eine neue Anfrage zu starten.
 */
@Component({
  selector: 'app-ki-anforderung-tools',
  imports: [
    KiAbdeckungComponent,
    KiButtonComponent,
    KiErsteNutzungComponent,
    KiEntwurfCardComponent,
    KiErgebnisAbgelaufenComponent,
    KiFehlerComponent,
    KiGesendetComponent,
    KiLaeuftComponent,
    KiPanelComponent,
    KiTestfallVorschlagDialogComponent,
    MatIconModule,
    TranslatePipe,
  ],
  providers: [KiRunService],
  templateUrl: './ki-anforderung-tools.component.html',
  styleUrl: './ki-anforderung-tools.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiAnforderungToolsComponent implements OnInit {
  protected readonly run = inject(KiRunService);
  private readonly translate = inject(TranslateService);
  private readonly anfragen = inject(KiAnfrageService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  /** Fehlt beim Anlegen: dann sind beide Schaltflächen mit Grund „ungespeichert“ gesperrt. */
  readonly requirementId = input<string | null>(null);
  readonly anfId = input<string>('');
  readonly anfTitel = input<string>('');
  readonly verknuepfungenUngespeichert = input(false);
  readonly darfVerknuepfen = input(false);
  readonly speichernFuerPruefung = input<(() => Observable<boolean>) | null>(null);
  /** Neu angelegter Testfall: Verknüpfung mit der Deckungsart des Backends, getrennt davon Key und Titel für die Zeile. */
  readonly testfallUebernommen = output<KiTestfallUebernommen>();
  /** Nach jeder Übernahme, auch bei 409 (schon übernommen, Testfall unbekannt): Verknüpfungen neu laden. */
  readonly verknuepfungenGeaendert = output<void>();
  /** Aus „Abdeckung prüfen“ neu angelegt: sofort aufnehmen, damit ein scheiterndes Neuladen sie nicht verliert. */
  readonly abdeckungVerknuepft = output<CoverageLinkDraft>();

  protected readonly dialogOffen = signal(false);
  protected readonly panelOffen = signal(false);
  /** Text im Dialog; bleibt nach Abbrechen und Senden für das nächste Öffnen stehen (page-ki.jsx:206). */
  protected readonly hinweisEntwurf = signal('');
  /** Kriterium der Lücke, aus der der Dialog geöffnet wurde; das direkte „Testfälle vorschlagen“ setzt es zurück. */
  protected readonly kriterium = signal<{ id: string; text: string } | null>(null);
  /** Hinweis der zuletzt gestarteten Anfrage, für den Untertitel des Panels. */
  protected readonly hinweis = signal('');
  /** Stand je Entwurfs-ID; „verworfen“ lebt nur hier und gilt bis zur nächsten Anfrage. */
  protected readonly staende = signal<Record<string, KiEntwurfStand>>({});
  /** Fertige Anfrage aus dem Postfach-Absprung; eine neue Anfrage verdrängt sie. */
  private readonly gespeichert = signal<KiAnfrage | null>(null);

  protected readonly sperrgrund = computed<KiSperrgrund | undefined>(() =>
    this.requirementId() ? undefined : 'ungespeichert',
  );
  protected readonly vorschlagState = computed<KiButtonState>(() => {
    if (this.sperrgrund()) return 'gesperrt';
    return this.run.zustand().art === 'laeuft' ? 'laeuft' : 'aktiv';
  });
  protected readonly kontext = computed(() => `${this.anfId()} · ${this.anfTitel()}`);
  protected readonly kriteriumText = computed(() => this.kriterium()?.text ?? null);
  protected readonly fehlerKey = computed(() => {
    const zustand = this.run.zustand();
    return zustand.art === 'fehler' ? zustand.textKey : null;
  });
  /** Erst nach dem Erst-Hinweis: bis dahin bleibt der Lauf „bereit“ und zeigt höchstens das gespeicherte Ergebnis. */
  protected readonly panelSichtbar = computed(
    () => this.panelOffen() && (this.run.zustand().art !== 'bereit' || this.gespeichert() !== null),
  );
  protected readonly fertigeAnfrage = computed<KiAnfrage | null>(() => {
    const zustand = this.run.zustand();
    if (zustand.art === 'fertig') return zustand.anfrage;
    return zustand.art === 'bereit' ? this.gespeichert() : null;
  });
  protected readonly ansicht = computed<EntwuerfeAnsicht | null>(() => {
    const anfrage = this.fertigeAnfrage();
    if (!anfrage || anfrage.ergebnisGeloescht) return null;
    const ergebnis = (anfrage.ergebnis ?? {}) as Partial<KiTestfallEntwuerfeErgebnis>;
    return {
      anfrageId: anfrage.id,
      entwuerfe: Array.isArray(ergebnis.entwuerfe) ? ergebnis.entwuerfe : [],
      ausgelassen: typeof ergebnis.ausgelassen === 'number' ? ergebnis.ausgelassen : 0,
    };
  });
  protected readonly gesamt = computed(() => this.ansicht()?.entwuerfe.length ?? 0);
  protected readonly offen = computed(() => {
    const staende = this.staende();
    return (this.ansicht()?.entwuerfe ?? []).filter((e) => (staende[e.entwurfId] ?? 'offen') === 'offen').length;
  });
  /** Die Inhalte benennt das Design für diese Funktion selbst (page-ki.jsx:316, AK11). */
  protected readonly gesendet = computed<KiGesendet | null>(() => {
    const gesendet = this.fertigeAnfrage()?.gesendet;
    if (!gesendet) return null;
    return {
      ...gesendet,
      inhalte: [
        this.translate.instant('ki.entwuerfe.gesendetAnforderung', { anfId: this.anfId() }),
        this.translate.instant('ki.entwuerfe.gesendetHinweis'),
      ],
    };
  });

  ngOnInit(): void {
    const anfrageId = this.route.snapshot.queryParamMap.get('kiAnfrage');
    const requirementId = this.requirementId();
    if (!anfrageId || !requirementId) return;
    this.anfragen
      .laden(this.projectId(), anfrageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (anfrage) => {
          // Das Postfach meldet nur fertige Anfragen; FEHLER oder LAEUFT ergäben ein leeres Panel ohne Erklärung.
          if (
            anfrage.art !== 'TESTFALL_ENTWUERFE' ||
            anfrage.kontextId !== requirementId ||
            anfrage.status !== 'FERTIG'
          ) {
            return;
          }
          this.gespeichert.set(anfrage);
          this.panelOffen.set(true);
        },
        // Ohne lesbare Anfrage bleibt nur der Button: ein leeres Panel erklärte nichts.
        error: () => undefined,
      });
  }

  protected vorschlagen(): void {
    this.kriterium.set(null);
    this.dialogOffen.set(true);
  }

  /**
   * Lücke aus „Abdeckung prüfen“, gesendet wird erst auf „Vorschläge erzeugen“. Mit Kriterium schlägt die KI nur
   * dafür vor und das Hinweisfeld bleibt frei; ohne steht der Aspekt als Hinweis im Dialog.
   */
  protected entwurfAusAbdeckung(luecke: KiAbdeckungLuecke): void {
    if (luecke.kriteriumId) {
      this.kriterium.set({ id: luecke.kriteriumId, text: luecke.titel });
      this.hinweisEntwurf.set('');
    } else {
      this.kriterium.set(null);
      this.hinweisEntwurf.set(luecke.titel);
    }
    this.dialogOffen.set(true);
  }

  protected erzeugen(hinweis: string): void {
    const requirementId = this.requirementId();
    const kriteriumId = this.kriterium()?.id;
    this.dialogOffen.set(false);
    if (!requirementId) return;
    this.gespeichert.set(null);
    this.hinweis.set(hinweis);
    this.staende.set({});
    this.panelOffen.set(true);
    this.run.starten({
      projectId: this.projectId(),
      art: 'testfall-entwuerfe',
      kontextTyp: 'REQUIREMENT',
      kontextId: requirementId,
      start: {
        pfad: `requirements/${encodeURIComponent(requirementId)}/ki/testfall-vorschlaege`,
        body: { ...(hinweis ? { hinweis } : {}), ...(kriteriumId ? { kriteriumId } : {}) },
      },
    });
  }

  protected standGeaendert(entwurf: KiTestfallEntwurf, stand: KiEntwurfStand): void {
    this.staende.update((staende) => ({ ...staende, [entwurf.entwurfId]: stand }));
    if (stand === 'uebernommen') this.verknuepfungenGeaendert.emit();
  }

  protected abdeckungVerknuepfungGemeldet(link: CoverageLinkDraft | null): void {
    if (link) this.abdeckungVerknuepft.emit(link);
    this.verknuepfungenGeaendert.emit();
  }

  protected angelegt(entwurf: KiTestfallEntwurf, ereignis: KiEntwurfAngelegt): void {
    this.testfallUebernommen.emit({
      link: { testCaseId: ereignis.testCaseId, coverageKind: DECKUNG[entwurf.art ?? 'POSITIV'] ?? 'POSITIVE' },
      testfall: { id: ereignis.testCaseId, key: ereignis.key, titel: ereignis.titel },
    });
  }
}
